import {
	Box,
	Button,
	Divider,
	Grid,
	Icon,
	IconButton,
	MenuItem,
	Modal,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { ICreateSinisterValues, ISinisterValues, SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { globalFormatCurrencyBRL, globalRemoveMask } from "../../../../shared/utils";

interface Props {
	open: boolean;
	onClose: (updateData: boolean) => void; // Passando uma prop para o componente pai
	sinisterId: string;
	onSuccess?: () => void;
}

interface IFormData {
	sinisterId: string;
	status: string;
	batchReason: string;
}

type TValueRow = {
	id: string;
};

type TValueRowDraft = {
	typeId?: string;
	reference?: string;
	dueDate?: string;
	value?: string;
};

const SINISTER_VALUE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: "rent_values", label: "Aluguel" },
	{ value: "water_values", label: "Água" },
	{ value: "fire_insurance", label: "Apólice seguro incêndio" },
	{ value: "condominium_values", label: "Condomínio" },
	{ value: "damage", label: "Danos ao Imóvel" },
	{ value: "energy_values", label: "Energia Elétrica" },
	{ value: "iptu", label: "IPTU" },
	{ value: "gas_values", label: "Gás" },
	{ value: "cleaning", label: "Limpeza" },
	{ value: "fine_on_rent", label: "Multa sobre Aluguel" },
	{ value: "fine_on_condominium", label: "Multa sobre valor do condomínio" },
	{ value: "painting", label: "Pintura" },
	{ value: "garantti_tax", label: "Taxa mensal Garantti" },
	{ value: "other", label: "Outros" },
];

const allowedFileSize = 50 * 1024 * 1024;
const allowedMimetypes = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.oasis.opendocument.text",
	"image/jpeg",
	"image/jpg",
	"image/png",
];

const validateAttachment = (file: File): string[] => {
	const errors: string[] = [];
	if (file.size > allowedFileSize) {
		errors.push(`Tamanho do arquivo excede ${(allowedFileSize / (1024 * 1024)).toFixed(0)}MB`);
	}
	if (!allowedMimetypes.includes(file.type)) {
		errors.push("Tipo de arquivo inválido");
	}
	return errors;
};

const createRowId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

export const ModalAddValues = ({ open, onClose, sinisterId, onSuccess }: Props) => {
	const { formRef } = useVForm();
	const [isLoading, setIsLoading] = useState(false);
	const [file, setFile] = useState<File>();
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const toastHandler = useToast();
	const [fileUploadedSuccessfully, setFileUploadedSuccessfully] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const ReferenceRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
	const [rows, setRows] = useState<TValueRow[]>([{ id: createRowId() }]);
	const [draftByRowId, setDraftByRowId] = useState<Record<string, TValueRowDraft>>({});

	const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);

	const handlePickAttachmentClick = () => {
		fileInputRef.current?.click();
	};

	const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0];
		if (!selected) return;

		const errors = validateAttachment(selected);
		if (errors.length > 0) {
			errors.forEach((msg) => toastHandler.present({ type: "error", position: "top-center", messageOrContent: msg }));
			if (fileInputRef.current) fileInputRef.current.value = "";
			setFile(undefined);
			return;
		}

		setFile(selected);
	};

	const handleRemoveAttachment = () => {
		setFile(undefined);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const addRow = () => {
		setRows((prev) => [...prev, { id: createRowId() }]);
	};

	const removeRow = (id: string) => {
		setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
		setDraftByRowId((prev) => {
			if (!prev[id]) return prev;
			const next = { ...prev };
			delete next[id];
			return next;
		});
	};

	const effectiveDraftRows = useMemo(() => {
		return rows
			.map((row) => ({
				rowId: row.id,
				draft: draftByRowId[row.id] ?? {},
			}))
			.filter(({ draft }) => {
				return !!(draft.typeId || draft.reference || draft.dueDate || draft.value);
			});
	}, [rows, draftByRowId]);

	const launchSummary = useMemo(() => {
		const items = effectiveDraftRows.map(({ rowId, draft }, index) => {
			const typeLabel = SINISTER_VALUE_TYPE_OPTIONS.find((o) => o.value === draft.typeId)?.label ?? "";
			const normalized = globalRemoveMask(draft.value ?? "");
			const numeric = Number(normalized) || 0;
			return {
				rowId,
				index: index + 1,
				typeId: draft.typeId ?? "",
				typeLabel,
				reference: draft.reference ?? "",
				dueDate: draft.dueDate ?? "",
				valueRaw: draft.value ?? "",
				valueNumeric: numeric,
				valueFormatted: globalFormatCurrencyBRL(normalized),
			};
		});

		const total = items.reduce((acc, item) => acc + item.valueNumeric, 0);
		return {
			items,
			count: items.length,
			totalNumeric: total,
			totalFormatted: total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
		};
	}, [effectiveDraftRows]);

	const rowValidationSchema: yup.SchemaOf<ICreateSinisterValues> = yup.object().shape({
		typeId: yup.string().required("Campo Obrigatório"),
		value: yup.string().required("Campo Obrigatório"),
		reference: yup.string().matches(ReferenceRegex, "Referência inválida. Digite no formato MM/AAAA.").required("Campo Obrigatório"),
		dueDate: yup.string().required("Campo Obrigatório"),
	});

	const handleSave = async (dados: IFormData) => {
		if (!file) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Erro"} content={"Obrigatório anexar um arquivo/comprovante"} />,
			});
			return;
		}

		setIsLoading(true);
		setIsUploading(true);
		setFileUploadedSuccessfully(false);
		formRef.current?.setErrors({});

		const formData = (formRef.current?.getData?.() ?? {}) as Record<string, any>;
		const batchReason = (formData.batchReason ?? "") as string;

		const candidates = rowIds.map((rowId) => {
			return {
				rowId,
				data: {
					typeId: (formData[`typeId_${rowId}`] ?? "") as string,
					value: (formData[`value_${rowId}`] ?? "") as string,
					reference: (formData[`reference_${rowId}`] ?? "") as string,
					dueDate: (formData[`dueDate_${rowId}`] ?? "") as string,
				},
			};
		});

		const effectiveRows = candidates.filter(({ data }) => {
			return !!(data.typeId || data.value || data.reference || data.dueDate);
		});

		if (effectiveRows.length === 0) {
			setIsLoading(false);
			setIsUploading(false);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Erro"} content={"Preencha ao menos uma linha para lançar"} />,
			});
			return;
		}

		const validationErros: IVFormErros = {};
		for (const row of effectiveRows) {
			try {
				await rowValidationSchema.validate(row.data, { abortEarly: false });
			} catch (errors: any) {
				const yupError = errors as yup.ValidationError;
				yupError.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[`${error.path}_${row.rowId}`] = error.message;
				});
			}
		}

		if (Object.keys(validationErros).length > 0) {
			setIsLoading(false);
			setIsUploading(false);
			formRef.current?.setErrors(validationErros);
			return;
		}

		const createdValueIds: string[] = [];
		try {
			for (const row of effectiveRows) {
				const payload: ISinisterValues = {
					sinisterId: sinisterId,
					status: "open",
					dueDate: format(new Date(row.data.dueDate + " 03:00:00"), "yyyy-MM-dd"),
					value: globalRemoveMask(row.data.value),
					reason: batchReason,
					reference: row.data.reference,
					typeId: row.data.typeId,
					file,
				};

				const result = await SinisterService.createValue(payload, file);
				const createdId = (result?.id ?? result?.data?.id) as string | undefined;
				if (typeof createdId === "string" && createdId.length > 0) createdValueIds.push(createdId);
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title={"Sucesso"} content={"Registros gravados com sucesso"} />,
			});
			setFileUploadedSuccessfully(true);
			setFile(undefined);
			if (fileInputRef.current) fileInputRef.current.value = "";
			onSuccess && onSuccess();
			onClose(true);
		} catch (e: any) {
			// best-effort rollback (sem mexer no backend): cancela o que foi criado antes do erro
			if (createdValueIds.length > 0) {
				await Promise.all(
					createdValueIds.map((id) =>
						SinisterService.updateValueCanceled(id, {
							reasonCancel: "Rollback: falha no lançamento em lote (modal planilha).",
						}),
					),
				);
			}

			toastHandler.present({
				type: "error",
				messageOrContent: e?.message ?? "Erro ao lançar valores",
				position: "top-center",
			});
		} finally {
			setIsLoading(false);
			setIsUploading(false);
		}
	};

	return (
		<Modal open={open} onClose={() => onClose(false)}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					width: "96%",
					maxWidth: 1024,
					maxHeight: "90vh",
					overflow: "hidden",
					borderRadius: 2,
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Header fixo */}
				<Box
					sx={{
						px: 3,
						py: 2,
						borderBottom: "1px solid",
						borderColor: "divider",
						flexShrink: 0,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 2,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant={"h5"} sx={{ fontWeight: 300 }} color={"primary.main"}>
							Adicionar valores ao sinistro
						</Typography>
						<Typography variant={"body2"} color={"text.secondary"} sx={{ mt: 0.5 }}>
							Insira uma ou mais linhas e anexe um único comprovante.
						</Typography>
					</Box>
					<IconButton onClick={() => onClose(false)} sx={{ mt: -0.5, mr: -1 }}>
						<Icon>close</Icon>
					</IconButton>
				</Box>

				{/* Body rolável */}
				<Box sx={{ px: 3, py: 2, overflowY: "auto", overflowX: "hidden", flex: 1 }}>
					<VForm id="formIdSave" placeholder="" ref={formRef} onSubmit={handleSave} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
						<Grid container direction="column" padding={1} spacing={2}>
							<Grid item xs={12}>
								<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
									<Table
										size="small"
										stickyHeader
										aria-label="Tabela de lançamentos"
										sx={{
											minWidth: 860,
											tableLayout: "fixed",
											"& .MuiTableCell-root": { px: 1, py: 1 },
										}}
									>
										<TableHead>
											<TableRow>
												<TableCell sx={{ width: 36 }}>#</TableCell>
												<TableCell sx={{ width: 320 }}>Tipo</TableCell>
												<TableCell sx={{ width: 140 }}>Competência</TableCell>
												<TableCell sx={{ width: 148 }}>Vencimento</TableCell>
												<TableCell sx={{ width: 160 }}>Valor</TableCell>
												<TableCell align="center" sx={{ width: 56 }}></TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{rows.map((row, index) => (
												<TableRow key={row.id}>
													<TableCell>
														<Typography variant="body2" color="text.secondary">
															{index + 1}
														</Typography>
													</TableCell>
													<TableCell>
														<VTextField
															fullWidth
															hiddenLabel
															label=""
															name={`typeId_${row.id}`}
															disabled={isLoading}
															select
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], typeId: nextValue },
																}));
															}}
														>
															<MenuItem value={""} disabled>
																Selecione…
															</MenuItem>
															{SINISTER_VALUE_TYPE_OPTIONS.map((opt) => (
																<MenuItem key={opt.value} value={opt.value}>
																	{opt.label}
																</MenuItem>
															))}
														</VTextField>
													</TableCell>
													<TableCell>
														<VMaskTextField
															fullWidth
															hiddenLabel
															placeholder={"MM/AAAA"}
															label=""
															name={`reference_${row.id}`}
															disabled={isLoading}
															mask="mesano"
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], reference: nextValue },
																}));
															}}
														/>
													</TableCell>
													<TableCell>
														<VTextField
															fullWidth
															hiddenLabel
															label=""
															name={`dueDate_${row.id}`}
															disabled={isLoading}
															type="date"
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], dueDate: nextValue },
																}));
															}}
														/>
													</TableCell>
													<TableCell>
														<VMaskTextField
															fullWidth
															hiddenLabel
															placeholder="0,00"
															label=""
															name={`value_${row.id}`}
															disabled={isLoading}
															mask="real"
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], value: nextValue },
																}));
															}}
														/>
													</TableCell>
													<TableCell align="center">
														<Tooltip title={rows.length <= 1 ? "Mínimo de 1 linha" : "Remover linha"}>
															<span>
																<IconButton
																	size="small"
																	onClick={() => removeRow(row.id)}
																	disabled={isLoading || rows.length <= 1}
																>
																	<Icon>delete</Icon>
																</IconButton>
															</span>
														</Tooltip>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Grid>

							<Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="text.secondary">
									{rows.length} {rows.length === 1 ? "linha" : "linhas"}
								</Typography>
								<Button variant="outlined" onClick={addRow} disabled={isLoading} startIcon={<Icon>add</Icon>}>
									Adicionar linha
								</Button>
							</Grid>
						</Grid>

						<Grid container item direction="column" spacing={1}>
							<Grid item>
								<Paper
									variant="outlined"
									sx={{
										p: 1.5,
										borderRadius: 2,
										mb: 1,
										backgroundColor: file ? "#f6fffa" : "#fff",
										borderLeft: `4px solid ${file ? "#4caf50" : "transparent"}`,
									}}
								>
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
												Comprovante (arquivo único)
											</Typography>
											<Typography variant="caption" color="text.secondary" noWrap>
												PDF, DOC, DOCX, ODT, PNG, JPG · até {(allowedFileSize / (1024 * 1024)).toFixed(0)}MB
											</Typography>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Button
												variant={file ? "outlined" : "contained"}
												onClick={handlePickAttachmentClick}
												disabled={isLoading || isUploading}
											>
												{file ? "Trocar arquivo" : "Selecionar arquivo"}
											</Button>
											<IconButton size="small" onClick={handleRemoveAttachment} disabled={!file || isLoading || isUploading}>
												<Icon>delete</Icon>
											</IconButton>
										</Box>
									</Box>

									{file && (
										<Typography variant="body2" sx={{ mt: 1 }} noWrap>
											{file.name}
										</Typography>
									)}
								</Paper>

								<input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleAttachmentChange} />
							</Grid>
						</Grid>

						<Grid item xs={12} sx={{ mt: 0.5, mb: 1 }}>
							<VTextField
								fullWidth
								label="Observação (aplica a todas as linhas)"
								name="batchReason"
								maxRows={3}
								multiline
								disabled={isLoading}
								placeholder="Ex.: boleto único com aluguel + água + energia"
							/>
						</Grid>

						<Grid container item direction="column" spacing={1} sx={{ mt: 0.5 }}>
							<Grid item>
								<Paper elevation={0} sx={{ py: 2, borderRadius: 2, width: "100%" }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										Resumo do lançamento
									</Typography>
									<Grid container spacing={2} sx={{ mt: 0.5, width: "100%", mx: 0 }}>
										<Grid item xs={12} md={8}>
											<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "action.hover" }}>
												<Typography variant="caption" color="text.secondary">
													Itens ({launchSummary.count})
												</Typography>
												<Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
													{launchSummary.count > 0 ? (
														launchSummary.items.map((item, i) => (
															<Box key={item.rowId}>
																<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
																	<Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
																		<Box component="span" sx={{ typography: "caption", color: "text.secondary", mr: 1 }}>
																			{item.reference || "—"}
																		</Box>
																		<Box component="span" sx={{ fontWeight: 500 }}>
																			{item.typeLabel || "—"}
																		</Box>
																	</Typography>
																	<Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, textAlign: "right" }}>
																		{item.valueRaw ? item.valueFormatted : "—"}
																	</Typography>
																</Box>
																{i < launchSummary.items.length - 1 && <Divider sx={{ mt: 1 }} />}
															</Box>
														))
													) : (
														<Typography variant="body2" color="text.secondary">
															Preencha as linhas acima para ver o resumo.
														</Typography>
													)}
												</Box>
											</Paper>
										</Grid>

										<Grid item xs={12} md={4}>
											<Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
												<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
													<Typography variant="h6" sx={{ fontWeight: 700 }} color="primary.main">
														{launchSummary.totalFormatted}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Total lançado
													</Typography>
												</Box>
											</Paper>
										</Grid>
									</Grid>
								</Paper>
							</Grid>
						</Grid>
					</VForm>
				</Box>

				{/* Footer fixo */}
				<Box
					sx={{
						px: 3,
						py: 2,
						borderTop: "1px solid",
						borderColor: "divider",
						flexShrink: 0,
						display: "flex",
						justifyContent: "center",
						gap: 2,
					}}
				>
					<Button color="inherit" disableElevation variant={"contained"} startIcon={<Icon>arrow_back_Ico</Icon>} onClick={() => onClose(false)}>
						{"Voltar"}
					</Button>
					<Button
						color="primary"
						disableElevation
						variant={"contained"}
						onClick={() => formRef.current?.submitForm()}
						disabled={isUploading || isLoading}
					>
						{"Confirmar"}
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};
